import { withMonth, type Asset, type AssetPayment, type MonthIndex } from '@finsim/model';
import { Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { KeyDateMonthField, RateField } from '../../../entities/project';
import {
  Button,
  EditOnly,
  Field,
  IconButton,
  MoneyField,
  PercentField,
  Section,
  Switch,
} from '../../../shared/ui';
import {
  addPayment,
  defaultPayments,
  maxInstalmentShare,
  normalizePayments,
  withOptional,
} from '../lib';
import { WindowFields } from './common-fields';
import type { FormContext } from './context';

export function AssetForm({ item, ctx }: { item: Asset; ctx: FormContext }) {
  const { t } = useTranslation();
  const set = (next: Asset, field: string): void => {
    ctx.commit(next, field);
  };
  const { start: startMonth, horizonMonths } = ctx.project.assumptions;

  return (
    <>
      <Section title={t('inspector.sections.account')}>
        <Field label={t('fields.openingBalance')}>
          {({ id }) => (
            <MoneyField
              id={id}
              className="primary-field"
              value={item.openingBalance}
              symbol={ctx.symbol}
              onCommit={(openingBalance) => {
                set({ ...item, openingBalance }, 'openingBalance');
              }}
            />
          )}
        </Field>
        <WindowFields
          ctx={ctx}
          item={item}
          showEnd={false}
          startLabel={t('fields.opens')}
          onChange={set}
        />
        <Field label={t('fields.fundedFromCash')} hint={t('fields.fundedFromCashHint')}>
          {({ id, describedBy }) => (
            <div className="pt-1">
              <Switch
                id={id}
                aria-describedby={describedBy}
                checked={item.fundedFromCash}
                onCheckedChange={(fundedFromCash) => {
                  set(
                    withOptional(
                      { ...item, fundedFromCash },
                      'payments',
                      fundedFromCash ? item.payments : undefined,
                    ),
                    'fundedFromCash',
                  );
                }}
              />
            </div>
          )}
        </Field>
        {item.fundedFromCash && <Instalments item={item} ctx={ctx} />}
        <RateField
          label={t('fields.return')}
          project={ctx.project}
          value={item.return}
          onCommit={(r) => {
            set({ ...item, return: r }, 'return');
          }}
        />
      </Section>

      <Section title={t('fields.liquidate')}>
        <Field label={t('fields.liquidate')}>
          {({ id }) => (
            <div className="pt-1">
              <Switch
                id={id}
                checked={item.liquidateAt !== undefined}
                disabled={item.start + 1 >= horizonMonths}
                onCheckedChange={(on) => {
                  const at = Math.min(horizonMonths - 1, item.start + 120) as MonthIndex;
                  set(withMonth(item, 'liquidateAt', on ? at : undefined), 'liquidateAt');
                }}
              />
            </div>
          )}
        </Field>
        {item.liquidateAt !== undefined && (
          <Field label={t('fields.liquidateAt')}>
            {({ id }) => (
              <KeyDateMonthField
                id={id}
                aria-label={t('fields.liquidateAt')}
                value={item.liquidateAt ?? item.start}
                anchor={item.anchors?.liquidateAt}
                start={startMonth}
                min={item.start + 1}
                max={horizonMonths - 1}
                onCommit={(liquidateAt, anchor) => {
                  set(withMonth(item, 'liquidateAt', liquidateAt, anchor), 'liquidateAt');
                }}
              />
            )}
          </Field>
        )}
      </Section>
    </>
  );
}

/** The price paid in instalments, the last one paying the rest; the unpaid part is owed. */
function Instalments({ item, ctx }: { item: Asset; ctx: FormContext }) {
  const { t } = useTranslation();
  const { start: startMonth, horizonMonths } = ctx.project.assumptions;
  const setPayments = (payments: AssetPayment[] | undefined): void => {
    ctx.commit(withOptional(item, 'payments', payments), 'payments');
  };
  const payments = item.payments;
  const added = payments && addPayment(payments, horizonMonths);

  return (
    <>
      <Field label={t('fields.instalments')} hint={t('fields.instalmentsHint')}>
        {({ id, describedBy }) => (
          <div className="flex items-center justify-between gap-2 pt-1">
            <Switch
              id={id}
              aria-describedby={describedBy}
              checked={payments !== undefined}
              disabled={item.start + 1 >= horizonMonths}
              onCheckedChange={(on) => {
                setPayments(on ? defaultPayments(item.start, horizonMonths) : undefined);
              }}
            />
            {payments && (
              <EditOnly>
                <Button
                  size="sm"
                  variant="ghost"
                  leadingIcon={<Plus />}
                  disabled={!added}
                  onClick={() => {
                    if (added) setPayments(added);
                  }}
                >
                  {t('fields.addInstalment')}
                </Button>
              </EditOnly>
            )}
          </div>
        )}
      </Field>
      {payments?.map((p, index) => {
        const last = index === payments.length - 1;
        const update = (next: AssetPayment): void => {
          setPayments(normalizePayments(payments.map((x, i) => (i === index ? next : x))));
        };
        return (
          <div
            key={`${String(p.month)}-${String(index)}`}
            className="space-y-1.5 rounded-md border border-border-subtle p-2"
          >
            <div className="flex items-start gap-1.5">
              <KeyDateMonthField
                className="min-w-0 flex-1"
                aria-label={t('fields.instalmentMonth', { n: index + 1 })}
                value={p.month}
                anchor={p.anchors?.month}
                start={startMonth}
                min={item.start}
                max={horizonMonths - 1}
                onCommit={(month, anchor) => {
                  update(withMonth(p, 'month', month, anchor));
                }}
              />
              {payments.length > 1 && (
                <EditOnly>
                  <IconButton
                    label={t('fields.removeInstalment', { n: index + 1 })}
                    onClick={() => {
                      setPayments(normalizePayments(payments.filter((_, i) => i !== index)));
                    }}
                  >
                    <X />
                  </IconButton>
                </EditOnly>
              )}
            </div>
            <div className="grid grid-cols-2 items-center gap-1.5">
              {last ? (
                <p className="tabular px-1 text-sm text-text-2">
                  {t('fields.instalmentRest', { share: ctx.percent(p.share) })}
                </p>
              ) : (
                <PercentField
                  aria-label={t('fields.instalmentShare', { n: index + 1 })}
                  value={p.share}
                  min={0.01}
                  max={maxInstalmentShare(payments, index)}
                  onCommit={(share) => {
                    update({ ...p, share });
                  }}
                />
              )}
              <p className="tabular text-right text-xs text-text-3">
                {ctx.money(p.share * item.openingBalance)}
              </p>
            </div>
          </div>
        );
      })}
    </>
  );
}
